import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FieldExpression, NodeExpression, toString as luceneToString } from 'lucene';
import { firstValueFrom } from 'rxjs';

import { PaginatedResult } from '../models/paginated-result';
import { API_SALESDOC_SERVER_URL } from '../../config';
import { FieldOfLegalDocument, LegalDocument, LegalDocumentFile } from '../models/legal-document';
import { OrderBy } from '../utils/constant';

import { ConnectionService } from './connection.service';

const GET_LEGAL_DOCUMENT_URL = `${ API_SALESDOC_SERVER_URL }/v2/legalDocuments`;
const GET_LEGAL_DOCUMENT_FILE_URL = `${ API_SALESDOC_SERVER_URL }/v2/legalDocuments`;

export const LEGAL_DOCUMENT_FIELD: FieldOfLegalDocument[] =
	['id', 'documentType', 'files', 'services', 'userAgreements'];
export const TERM_OF_USE = 'TOU';
export const GENERAL_SALES_CONDITIONS = 'GSC';
export const SALES_FRAMEWORK = 'SF';

@Injectable({ providedIn: 'root' })
export class LegalDocumentService {
	constructor(
		private readonly http: HttpClient,
		private readonly connectionService: ConnectionService
	) {}

	getLegalDocument(
		fields: readonly FieldOfLegalDocument[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<string>[] | OrderBy<string>,
		onlyDistinct: boolean
	) {
		const url = new URL(GET_LEGAL_DOCUMENT_URL);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		if (filter) {
			params.append('filter', filter);
		}
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));

		params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));

		params.append('distinct', String(onlyDistinct));

		return this.http.get<PaginatedResult<LegalDocument>>(url.toString());
	}

	createLuceneFilter(legalDocumentType: string) {
		const customerArticleReferenceField: FieldExpression = {
			field: 'documentType',
			term: legalDocumentType,
			quoted: true,
		};

		return luceneToString(customerArticleReferenceField);
	}

	createLuceneAgreementFilter(legalDocumentService: string) {
		const gscDocumentTypeNode: FieldExpression = {
			field: 'documentType',
			term: GENERAL_SALES_CONDITIONS,
			quoted: false
		};

		const sfDocumentTypeNode: FieldExpression = {
			field: 'documentType',
			term: SALES_FRAMEWORK,
			quoted: false
		};

		const codeServiceNode: FieldExpression = {
			field: 'services.codeService',
			term: legalDocumentService,
			quoted: false
		};

		const sfCodeServiceNode = {
			left: sfDocumentTypeNode,
			operator: 'AND',
			right: codeServiceNode,
			parenthesized: true
		};

		const allNode: NodeExpression = {
			left: sfCodeServiceNode,
			operator: 'OR',
			right: gscDocumentTypeNode,
			parenthesized: false
		};
		return luceneToString(allNode);
	}

	getFileURL(file: LegalDocumentFile, id: number) {
		const url = new URL(`${ GET_LEGAL_DOCUMENT_FILE_URL }/${ id }/attachments/${ file.id }/${ file.fileName }`);
		const params = url.searchParams;
		params.append('access_token', this.connectionService.getDownloadAccessToken());
		return url.toString();
	}

	async saveUserAgreement(legalDocumentId: number) {
		const url = new URL(`${ GET_LEGAL_DOCUMENT_URL }/${ legalDocumentId }/agreement`);
		await firstValueFrom(this.http.post(url.toString(), legalDocumentId));
	}
}
